import {
  ProductV2,
  CountryConfiguration,
  PurchaseDraftWithProducts,
  PaymentProvider,
  PURCHASE_DRAFT_MAX_SKU_COUNT,
  MAX_ROW_SKU_COUNT,
} from '@common'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { api } from '@/api'
import { AppThunk } from '@/store'

import { EligibilityStatus } from '../../../../common/src/models/scid'
import { compareCartRows } from '../../utils/compareCartRows'
import { gtmAddToCart, gtmPurchaseComplete, gtmRemoveFromCart } from '../../utils/gtm'
import logger from '../../utils/logger'
import { normalizeCreatorBoostCode } from '../../utils/normalizeCreatorBoostCode'
import { LOGIN_STATUS, resetUser } from '../user/userSlice'

export interface CheckoutRow {
  product: ProductV2
  quantity: number
  eligibility?: EligibilityStatus
}

export interface CheckoutState {
  draft: {
    id: string
    revision: number
    updatedAt: number
  } | null
  acceptedTermsAndConditions: boolean
  billingCountry: CountryConfiguration | null
  creatorBoostCode: {
    value: string
    isValid: boolean
    validating: boolean
  }
  error: {
    title: string
    description: string
    extraDescription?: string
  } | null
  loadingPayment: boolean
  isUpdatingPaymentProvider: boolean
  rows: CheckoutRow[]
  meta: {
    item_list_id: string
    item_list_name: string
  } | null
  paymentProvider: PaymentProvider | null
}

export const initialState: CheckoutState = {
  acceptedTermsAndConditions: false,
  billingCountry: null,
  creatorBoostCode: {
    value: '',
    isValid: true,
    validating: false,
  },
  error: null,
  loadingPayment: false,
  isUpdatingPaymentProvider: false,
  rows: [],
  meta: null,
  draft: null,
  paymentProvider: null,
}

export const completePurchase =
  (purchaseId: string): AppThunk =>
  async (dispatch, getState) => {
    const checkoutMeta = getState().checkout.meta
    const { data: purchase } = await dispatch(api.endpoints.fetchPurchase.initiate(purchaseId))
    if (!purchase) {
      return
    }
    gtmPurchaseComplete(purchase, checkoutMeta)
    dispatch(clearCheckout())
  }

export const addProductThunk =
  (product: ProductV2): AppThunk<boolean> =>
  (dispatch, getState) => {
    if (
      getState().checkout.rows.reduce((acc, { quantity }) => acc + quantity, 0) >= PURCHASE_DRAFT_MAX_SKU_COUNT ||
      getState().checkout.rows.find((row) => row.product.id === product.id)?.quantity === MAX_ROW_SKU_COUNT
    ) {
      return false
    }
    dispatch(checkoutSlice.actions.addProduct({ product, cartEnabled: getState().config.features.enableCart }))
    gtmAddToCart(product)
    return true
  }

export const removeProductThunk =
  (product: ProductV2): AppThunk =>
  (dispatch) => {
    dispatch(removeProduct(product))
    gtmRemoveFromCart(product)
  }

export const initializeCheckoutState: AppThunk = async (dispatch, getState) => {
  const log = logger.child({ label: 'initializeCheckoutState' })
  if (getState().user.loginStatus !== LOGIN_STATUS.LOGGED_IN) {
    log.debug({ msg: 'not logged in' })
    return
  }
  const market = getState().config.market
  if (!market) {
    log.debug({ msg: 'no billing country' })
    return
  }
  const clientRows = getState().checkout.rows
  const clientLanguage = getState().config.locale
  const clientCreatorBoost = getState().checkout.creatorBoostCode.value
  const clientCountryCode = market.countryCode

  const existingDraft = await dispatch(api.endpoints.getDraft.initiate()).unwrap()

  // If there is no existing draft, return
  if (!existingDraft) {
    log.debug({ msg: 'no existing draft', clientRows, clientCreatorBoost, clientCountryCode, clientLanguage })
    await dispatch(
      api.endpoints.updateDraft.initiate({
        countryCode: clientCountryCode,
        language: clientLanguage,
        rows: clientRows.map(({ product: { id: sku }, quantity }) => ({ sku, quantity })),
        creatorBoostCode: clientCreatorBoost ? clientCreatorBoost : undefined,
      }),
    )
    await dispatch(api.endpoints.getDraft.initiate()).unwrap()
    return
  }

  log.debug({
    msg: 'existing draft',
    draft: existingDraft,
    clientRows,
    clientCreatorBoost,
    clientCountryCode,
    clientLanguage,
  })

  // Resolve whether draft should be updated according to existing rows, creator boost code, market and language
  const updateRows = clientRows.length > 0 && compareCartRows(existingDraft.rows, clientRows) ? clientRows : false
  const updateBoostCode =
    normalizeCreatorBoostCode(clientCreatorBoost) !== normalizeCreatorBoostCode(existingDraft.creatorBoost)
      ? clientCreatorBoost
      : false
  const updateCountry = clientCountryCode !== existingDraft.country ? clientCountryCode : false
  const updateLanguage = clientLanguage !== existingDraft.language ? clientLanguage : false

  if (updateRows || updateBoostCode || updateCountry || updateLanguage) {
    log.debug({ msg: 'updating draft', updateRows, updateBoostCode, updateLanguage, updateCountry })
    const updatedDraftResponse = await dispatch(
      api.endpoints.updateDraft.initiate({
        countryCode: market.countryCode,
        language: updateLanguage ? updateLanguage : existingDraft.language,
        rows: updateRows ? updateRows.map(({ product: { id: sku }, quantity }) => ({ sku, quantity })) : undefined,
        creatorBoostCode: clientCreatorBoost ? clientCreatorBoost : undefined,
      }),
    )
    if ('error' in updatedDraftResponse) {
      log.error({ msg: 'error updating draft', error: updatedDraftResponse.error })
      const draftResponse = await dispatch(api.endpoints.getDraft.initiate()).unwrap()
      dispatch(checkoutSlice.actions.syncWithDraft({ market, draft: draftResponse }))
    } else {
      log.debug({ msg: 'syncing updated draft', draft: updatedDraftResponse.data })
      dispatch(checkoutSlice.actions.syncWithDraft({ market, draft: updatedDraftResponse.data }))
    }
  } else {
    log.debug({ msg: 'syncing existing draft', draft: existingDraft })
    dispatch(checkoutSlice.actions.syncWithDraft({ market, draft: existingDraft }))
  }
}

export const clearCheckout = (): AppThunk => async (dispatch) => {
  dispatch(resetCheckoutState())
  dispatch(api.util.invalidateTags(['Draft', 'Session']))
}

export const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    reCreateCart: (state: CheckoutState) => {
      state.draft = null
    },
    addProduct: (state: CheckoutState, action: PayloadAction<{ product: ProductV2; cartEnabled: boolean }>) => {
      const { product, cartEnabled } = action.payload
      const idx = state.rows.findIndex((row) => row.product.id === product.id)
      if (idx > -1) {
        state.rows[idx].quantity = Math.min(state.rows[idx].quantity + 1, 10)
      } else {
        if (!cartEnabled) {
          state.rows = []
        }
        state.rows.push({ product, quantity: 1 })
      }
    },
    removeProduct: (state: CheckoutState, action: PayloadAction<ProductV2>) => {
      const idx = state.rows.findIndex((r) => r.product.id === action.payload.id)
      if (idx === -1) {
        return state
      }
      state.rows[idx].quantity--
      if (state.rows[idx].quantity < 1) {
        state.rows.splice(idx, 1)
      }
    },
    resetCheckoutState: (state) => {
      return {
        ...initialState,
        billingCountry: state.billingCountry,
        creatorBoostCode: state.creatorBoostCode,
      }
    },
    toggleTermsAndConditions: (state) => {
      state.acceptedTermsAndConditions = !state.acceptedTermsAndConditions
    },
    setBillingCountry: (state: CheckoutState, action: PayloadAction<CheckoutState['billingCountry'] | null>) => {
      state.billingCountry = action.payload
    },
    setCreatorBoostCode: (state: CheckoutState, action: PayloadAction<CheckoutState['creatorBoostCode']>) => {
      state.creatorBoostCode = action.payload
    },
    setError: (state: CheckoutState, action: PayloadAction<CheckoutState['error']>) => {
      state.error = action.payload
    },
    setCheckoutMeta: (state: CheckoutState, action: PayloadAction<CheckoutState['meta']>) => {
      state.meta = action.payload
    },
    syncWithDraft: (
      state: CheckoutState,
      action: PayloadAction<{ draft: PurchaseDraftWithProducts | null; market: CountryConfiguration | null }>,
    ) => {
      if (!action.payload.draft) {
        return
      }
      if (action.payload.market && state.billingCountry?.countryCode !== action.payload.market.countryCode) {
        state.billingCountry = action.payload.market
      }
      if (action.payload.draft.creatorBoost) {
        state.creatorBoostCode = {
          value: action.payload.draft.creatorBoost,
          isValid: true,
          validating: false,
        }
      } else {
        state.creatorBoostCode = {
          value: '',
          isValid: true,
          validating: false,
        }
      }
      state.rows = action.payload.draft.rows
      state.draft = {
        id: action.payload.draft.id,
        revision: action.payload.draft.revision,
        updatedAt: action.payload.draft.updatedAt,
      }
      state.paymentProvider = action.payload.draft.paymentProvider
    },
    changePaymentProvider: (state: CheckoutState, action: PayloadAction<CheckoutState['paymentProvider']>) => {
      state.paymentProvider = action.payload
      state.isUpdatingPaymentProvider = true
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(resetUser.match, (state) => {
        state.draft = null
        state.acceptedTermsAndConditions = false
        state.error = null
      })
      .addMatcher(api.endpoints.updateDraft.matchFulfilled, (state, action) => {
        state.draft = {
          id: action.payload.id,
          revision: action.payload.revision,
          updatedAt: action.payload.updatedAt,
        }
        state.paymentProvider = action.payload.paymentProvider
        state.isUpdatingPaymentProvider = false
      })
      .addMatcher(api.endpoints.checkoutAdyenSession.matchFulfilled, (state, action) => {
        if (action.payload.session) {
          state.draft = {
            id: action.payload.id,
            revision: action.payload.revision,
            updatedAt: state.draft?.updatedAt ?? Date.now(),
          }
        }
      })
      .addMatcher(api.endpoints.checkoutAdyenPayment.matchFulfilled, (state, action) => {
        state.draft = {
          id: action.payload.id,
          revision: action.payload.revision,
          updatedAt: state.draft?.updatedAt ?? Date.now(),
        }
      })
  },
})

export const {
  addProduct,
  reCreateCart,
  removeProduct,
  setCreatorBoostCode,
  setError,
  toggleTermsAndConditions,
  setCheckoutMeta,
  setBillingCountry,
  syncWithDraft,
  resetCheckoutState,
  changePaymentProvider,
} = checkoutSlice.actions

export default checkoutSlice.reducer
